import { FC, useContext, useId } from "react";
import { ADEQUACY_FAILING_GROUP, ADEQUACY_OUTCOME_HELPER_TEXT, ADEQUACY_OUTCOME_ORDER, ADEQUACY_PASSING_OPTION } from "@app/projects/network-assessment/multi-map-export/utils/adequacyFilterOptions";
import { SwatchCheckboxGroup } from "@app/projects/network-assessment/multi-map-export/components/SwatchCheckboxGroup";
import { SwatchLabel } from "@app/projects/network-assessment/multi-map-export/components/SwatchLabel";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { MultiMapExportAdequacyOutcome } from "@app/projects/network-assessment/multi-map-export/interfaces";
import { MultiMapExportContext } from "@app/projects/network-assessment/multi-map-export/MultiMapExportContext";
import { MultiMapExportSection } from "@app/projects/network-assessment/multi-map-export/components/MultiMapExportSection";
import { DEFAULT_ADEQUACY_OUTCOMES, MultiMapExportAction } from "@app/projects/network-assessment/multi-map-export/state-management/multiMapExportStateManagement";
import { toggleAdequacyOutcome } from "@app/projects/network-assessment/multi-map-export/utils/adequacyFilterSelection";

const SECTION_NUMBER = 4;
const TEST_ID_PREFIX = "multi-map-export__adequacy-outcome";

export const AdequacyFilterSection: FC<React.PropsWithChildren<unknown>> = () => {
  const { selections, selectionsChanged } = useContext(MultiMapExportContext);
  const outcomeLabelId = useId();

  const selectedOutcomes = selections?.selectedAdequacyOutcomes ?? DEFAULT_ADEQUACY_OUTCOMES;
  const showRequiredError = selectedOutcomes.length === 0;

  const handleOutcomesChange = (outcomes: MultiMapExportAdequacyOutcome[]) => selectionsChanged?.({
    type: MultiMapExportAction.AdequacyFilterChanged,
    value: { selectedAdequacyOutcomes: outcomes },
  });

  return (
    <MultiMapExportSection
      sectionNumber={SECTION_NUMBER}
      subHeader="Select which adequacy outcomes should produce a map."
      testId="multi-map-export__adequacy-filter-section"
      title="Adequacy Filter"
    >
      <div className="multi-map-export__field">
        <div aria-labelledby={outcomeLabelId} className="multi-map-export__adequacy-outcomes" role="group">
          <span className="multi-map-export__field-label" id={outcomeLabelId}>Adequacy Outcome</span>
          <QESCheckbox
            checked={selectedOutcomes.includes(ADEQUACY_PASSING_OPTION.value)}
            containerClassName="multi-map-export__swatch-checkbox"
            label={<SwatchLabel borderColor={ADEQUACY_PASSING_OPTION.swatchBorderColor} color={ADEQUACY_PASSING_OPTION.swatchColor} iconName={ADEQUACY_PASSING_OPTION.swatchIcon} label={ADEQUACY_PASSING_OPTION.label} />}
            onChange={(event) => handleOutcomesChange(toggleAdequacyOutcome(selectedOutcomes, ADEQUACY_PASSING_OPTION.value, event.target.checked))}
            testId={`${TEST_ID_PREFIX}-option--${ADEQUACY_PASSING_OPTION.value}`}
          />
          <SwatchCheckboxGroup
            canonicalOrder={ADEQUACY_OUTCOME_ORDER}
            group={ADEQUACY_FAILING_GROUP}
            onChange={handleOutcomesChange}
            selectedValues={selectedOutcomes}
            testIdPrefix={TEST_ID_PREFIX}
          />
        </div>
        <p className="multi-map-export__field-helper-text">{ADEQUACY_OUTCOME_HELPER_TEXT}</p>
        {showRequiredError && (
          <p className="multi-map-export__field-error" data-testid="multi-map-export__adequacy-outcomes-error">Select at least one adequacy outcome.</p>
        )}
      </div>
    </MultiMapExportSection>
  );
};

export default AdequacyFilterSection;
